import type {
  NodeInfo,
  NodeDetail,
  ConsensusState,
  CRDTState,
  CRDTOperation,
  NetworkTopology,
  SecurityEvent,
  SystemConfig,
  Alert,
  HealthResponse,
  ApiResponse,
} from '../types';
// Operator sign-in contract (sso-04). The generated socket-cloud types are the
// only declaration of these shapes; nothing is restated by hand.
import type * as SocketCloudSchemaTypes from '@aaimplatform/socket-cloud-types/generated/socket-cloud.types';
import type * as SocketCloudOrganizationTypes from '@aaimplatform/socket-cloud-types/generated/socket-cloud-organization.types';
import type * as SocketCloudAlertsMonitoringTypes from '@aaimplatform/socket-cloud-types/generated/socket-cloud-alerts-monitoring.types';
// Mock data imports removed - all data comes from database

/**
 * Enterprise-grade API client for SocketCloud Console
 * Integrates with existing socket-cloud REST/GraphQL APIs
 */
// Add import.meta.env type declaration
declare global {
  interface ImportMetaEnv {
    VITE_API_URL?: string;
    VITE_WS_URL?: string;
  }
}

// ---------------------------------------------------------------------------
// Sprint C10 sweep: auth-broken detection + bounce-to-login.
// Mirrors the platform-admin guard landed in bf402818f. socket-cloud-api
// does not currently enforce requireTenantScope, but uniform behavior keeps
// future tenant-scope rollouts from creating silent-failure modes here.
// ---------------------------------------------------------------------------

async function isAuthBrokenResponse(response: Response): Promise<boolean> {
  if (response.status === 401) return true;
  if (response.status === 400) {
    try {
      const body = (await response.clone().json()) as
        | { error?: { code?: string }; code?: string }
        | undefined;
      const code = body?.error?.code ?? body?.code;
      return code === 'TENANT_SCOPE_REQUIRED' || code === 'AUTH_REQUIRED';
    } catch {
      return false;
    }
  }
  return false;
}

function bounceToLogin(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem('authToken');
    window.localStorage.removeItem('refreshToken');
  } catch {
    // ignore
  }
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

async function checkAuthBroken(response: Response): Promise<void> {
  if (await isAuthBrokenResponse(response)) {
    bounceToLogin();
  }
}

class ApiClient {
  private baseUrl: string;
  private timeout: number;
  private retryAttempts: number;

  constructor(baseUrl = import.meta.env.VITE_API_URL || '/api/v1', timeout = 30000, retryAttempts = 3) {
    // Use relative URL by default for better containerization support
    this.baseUrl = baseUrl;
    this.timeout = timeout;

    // Configure retry attempts based on environment
    if (!import.meta.env.VITE_API_URL) {
      this.retryAttempts = 1;
      console.info('SocketCloud Console: Using relative API path /api/v1');
    } else {
      this.retryAttempts = retryAttempts;
      console.info('SocketCloud Console: Using configured API URL:', baseUrl);
    }
  }

  public async request<T>(
    endpoint: string, 
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    console.log('[API] REQUEST START:', {
      endpoint,
      url,
      method: options.method || 'GET',
      timestamp: new Date().toISOString()
    });
    
    const requestStart = performance.now();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.error('[API] Request timeout for:', endpoint);
      controller.abort();
    }, this.timeout);

    // Browser-token-storage hardening: the SPA holds no bearer; the sc-auth-token
    // HttpOnly cookie authenticates via credentials:'include' below.
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      } as HeadersInit,
      credentials: 'include', // Include cookies for authentication
      signal: controller.signal,
      ...options,
    };

    console.log('[API] Request options:', {
      headers: defaultOptions.headers,
      credentials: defaultOptions.credentials,
      timeout: this.timeout,
      retryAttempts: this.retryAttempts
    });

    let lastError: Error | null = null;

    for (let attempt = 0; attempt < this.retryAttempts; attempt++) {
      const attemptStart = performance.now();
      console.log(`[API] Attempt ${attempt + 1}/${this.retryAttempts} for ${endpoint}`);
      
      try {
        console.log('[API] Sending fetch request...');
        const fetchStart = performance.now();
        const response = await fetch(url, defaultOptions);
        const fetchTime = performance.now() - fetchStart;
        
        console.log('[API] Fetch response received in', fetchTime.toFixed(2), 'ms:', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          ok: response.ok
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          console.error('[API] HTTP error response:', {
            status: response.status,
            statusText: response.statusText,
            url: response.url
          });

          // Sprint C10 sweep: bounce to /login if the auth state is broken.
          await checkAuthBroken(response);

          // For 404s when no API URL is configured, silently fall back to mock data
          if (response.status === 404 && !import.meta.env.VITE_API_URL) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // Check content type before parsing
        const contentType = response.headers.get('content-type');
        console.log('[API] Response content-type:', contentType);
        
        if (!contentType || !contentType.includes('application/json')) {
          console.error('[API] Invalid content type:', contentType);
          throw new Error(`Invalid response type: ${contentType || 'unknown'}. Expected JSON.`);
        }

        console.log('[API] Parsing JSON response...');
        const parseStart = performance.now();
        const data = await response.json();
        const parseTime = performance.now() - parseStart;
        
        const totalTime = performance.now() - requestStart;
        console.log('[API] REQUEST SUCCESS in', totalTime.toFixed(2), 'ms:', {
          endpoint,
          parseTime: parseTime.toFixed(2) + 'ms',
          dataSize: JSON.stringify(data).length + ' chars',
          attempt: attempt + 1
        });
        
        return {
          success: true,
          data,
          timestamp: new Date(),
        };
      } catch (error) {
        const attemptTime = performance.now() - attemptStart;
        lastError = error as Error;
        
        console.error(`[API] Request attempt ${attempt + 1} failed after ${attemptTime.toFixed(2)}ms:`, {
          endpoint,
          error: lastError.message,
          name: lastError.name,
          stack: lastError.stack?.split('\n')[0]
        });
        
        // Log the error but don't fall back to mock data
        console.error(`API request failed for ${endpoint}:`, (error as Error).message);
        
        // Don't retry on client errors (4xx) except 404 and 429
        if (error instanceof Error && error.message.includes('HTTP 4') && 
            !error.message.includes('HTTP 404') && 
            !error.message.includes('HTTP 429')) {
          break;
        }

        // Special handling for rate limiting
        if (error instanceof Error && error.message.includes('HTTP 429')) {
          // Wait longer for rate limit errors
          const retryAfter = 60000; // 60 seconds
          console.warn(`Rate limited on ${endpoint}, waiting ${retryAfter}ms before retry`);
          if (attempt < this.retryAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, retryAfter));
          }
        } else if (attempt < this.retryAttempts - 1) {
          // Regular exponential backoff for other errors
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }

    clearTimeout(timeoutId);
    return {
      success: false,
      error: lastError?.message || 'Unknown error',
      timestamp: new Date(),
    };
  }


  // Dashboard APIs
  // GET /api/v1/dashboard/metrics (socket-cloud.openapi.yaml, GH #2709 D2).
  // Before GH #2709 this read the unversioned /api/dashboard/metrics. The body
  // is a { success, data } envelope; callers receive its data.
  async getDashboardMetrics(): Promise<
    ApiResponse<SocketCloudSchemaTypes.components['schemas']['SocketCloudDashboardMetrics']>
  > {
    console.log('[API] getDashboardMetrics called');
    const url = '/api/v1/dashboard/metrics';
    
    try {
      console.log('[API] Fetching dashboard metrics from:', url);
      const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      
      console.log('[API] Dashboard metrics response status:', res.status);

      if (!res.ok) {
        await checkAuthBroken(res);
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const body: SocketCloudSchemaTypes.paths['/api/v1/dashboard/metrics']['get']['responses'][200]['content']['application/json'] =
        await res.json();
      console.log('[API] Dashboard metrics data:', body);

      return { success: true, data: body.data, timestamp: new Date() };
    } catch (error) {
      console.error('[API] Dashboard metrics error:', error);
      return {
        success: false,
        error: (error as Error).message,
        timestamp: new Date(),
      };
    }
  }

  // Node Management APIs
  // GET /api/v1/nodes response body: { nodes: NodeInfo[], total, leader?, term? }
  async getNodes(): Promise<
    ApiResponse<SocketCloudSchemaTypes.paths['/api/v1/nodes']['get']['responses'][200]['content']['application/json']>
  > {
    // GET /api/v1/nodes (socket-cloud.openapi.yaml). Before GH #2284 this read
    // /api/nodes, which socket-cloud-api never served.
    const url = '/api/v1/nodes';
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        await checkAuthBroken(response);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      return {
        success: true,
        data,
        timestamp: new Date(),
      };
    } catch (error) {
      clearTimeout(timeoutId);
      return {
        success: false,
        error: (error as Error).message,
        timestamp: new Date(),
      };
    }
  }

  async getNode(nodeId: string): Promise<ApiResponse<NodeDetail>> {
    return this.request<NodeDetail>(`/nodes/${nodeId}`);
  }

  async updateNode(nodeId: string, updates: Partial<NodeInfo>): Promise<ApiResponse<NodeInfo>> {
    return this.request<NodeInfo>(`/nodes/${nodeId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async removeNode(nodeId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/nodes/${nodeId}`, {
      method: 'DELETE',
    });
  }

  // Consensus APIs
  async getConsensusState(): Promise<ApiResponse<ConsensusState>> {
    return this.request<ConsensusState>('/consensus/state');
  }

  async triggerElection(): Promise<ApiResponse<void>> {
    return this.request<void>('/consensus/election', {
      method: 'POST',
    });
  }

  async stepDown(): Promise<ApiResponse<void>> {
    return this.request<void>('/consensus/step-down', {
      method: 'POST',
    });
  }

  // CRDT APIs
  async getCRDTStates(): Promise<ApiResponse<CRDTState[]>> {
    return this.request<CRDTState[]>('/crdt/states');
  }

  async getCRDTState(crdtId: string): Promise<ApiResponse<CRDTState>> {
    return this.request<CRDTState>(`/crdt/states/${crdtId}`);
  }

  // POST /api/v1/crdt/initialize (socket-cloud.openapi.yaml; GH #2712): the
  // CRDTInitializeRequest body requires the CRDT type and the node it starts on,
  // and the 201 answers CRDTInfo.
  async createCRDT(
    type: SocketCloudSchemaTypes.components['schemas']['CRDTType'],
    nodeId: string,
  ): Promise<ApiResponse<SocketCloudSchemaTypes.components['schemas']['CRDTInfo']>> {
    const body: SocketCloudSchemaTypes.components['schemas']['CRDTInitializeRequest'] = { type, nodeId };
    return this.request<SocketCloudSchemaTypes.components['schemas']['CRDTInfo']>('/crdt/initialize', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async updateCRDT(crdtId: string, operation: any): Promise<ApiResponse<CRDTState>> {
    return this.request<CRDTState>(`/crdt/states/${crdtId}/update`, {
      method: 'POST',
      body: JSON.stringify(operation),
    });
  }

  // CRDT Monitoring APIs
  async getCRDTOperations(timeRange?: string): Promise<ApiResponse<CRDTOperation[]>> {
    const params = timeRange ? `?timeRange=${timeRange}` : '';
    return this.request<CRDTOperation[]>(`/crdt/operations${params}`);
  }

  async getVectorClocks(): Promise<ApiResponse<{ nodeId: string; vectorClock: any; timestamp: Date }[]>> {
    return this.request<{ nodeId: string; vectorClock: any; timestamp: Date }[]>('/crdt/vector-clocks');
  }

  async getCRDTMetrics(nodeId?: string, crdtType?: string): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams();
    if (nodeId) {
params.append('nodeId', nodeId);
}
    if (crdtType) {
params.append('crdtType', crdtType);
}
    
    const query = params.toString();
    return this.request<any[]>(`/crdt/metrics${query ? `?${query}` : ''}`);
  }

  async getCRDTPerformanceData(timeRange = '6h'): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/crdt/performance?timeRange=${timeRange}`);
  }

  async getSynchronizationStates(): Promise<ApiResponse<any[]>> {
    return this.request<any[]>('/crdt/sync-states');
  }

  async getMergeOperations(limit = 100): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/crdt/merge-operations?limit=${limit}`);
  }

  async getConvergenceMetrics(): Promise<ApiResponse<any[]>> {
    return this.request<any[]>('/crdt/convergence-metrics');
  }

  async getReplicationLatencies(): Promise<ApiResponse<any[]>> {
    return this.request<any[]>('/crdt/replication-latencies');
  }

  async getCRDTAnomalies(): Promise<ApiResponse<any[]>> {
    return this.request<any[]>('/crdt/anomalies');
  }

  async acknowledgeAnomaly(anomalyId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/crdt/anomalies/${anomalyId}/acknowledge`, {
      method: 'POST',
    });
  }

  async applyCRDTOptimization(optimizationId: string, params: any): Promise<ApiResponse<void>> {
    return this.request<void>(`/crdt/optimizations/${optimizationId}/apply`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // Network Topology APIs
  async getNetworkTopology(): Promise<ApiResponse<NetworkTopology>> {
    return this.request<NetworkTopology>('/network/topology');
  }

  async triggerPartitionHealing(): Promise<ApiResponse<void>> {
    return this.request<void>('/network/heal-partitions', {
      method: 'POST',
    });
  }

  // Security APIs
  async getSecurityEvents(limit = 100): Promise<ApiResponse<SecurityEvent[]>> {
    return this.request<SecurityEvent[]>(`/security/events?limit=${limit}`);
  }

  async acknowledgeSecurityEvent(eventId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/security/events/${eventId}/acknowledge`, {
      method: 'POST',
    });
  }

  async resolveSecurityEvent(eventId: string, resolution: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/security/events/${eventId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ resolution }),
    });
  }

  // Configuration APIs
  async getSystemConfig(): Promise<ApiResponse<SystemConfig>> {
    return this.request<SystemConfig>('/config/system');
  }

  async updateSystemConfig(config: Partial<SystemConfig>): Promise<ApiResponse<SystemConfig>> {
    return this.request<SystemConfig>('/config/system', {
      method: 'PATCH',
      body: JSON.stringify(config),
    });
  }

  async validateConfig(config: SystemConfig): Promise<ApiResponse<{ valid: boolean; errors: string[] }>> {
    return this.request<{ valid: boolean; errors: string[] }>('/config/validate', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  // Alert APIs
  async getAlerts(filter?: { severity?: string; type?: string; acknowledged?: boolean }): Promise<ApiResponse<Alert[]>> {
    console.log('[API] getAlerts called with filter:', filter);
    const params = new URLSearchParams();
    if (filter?.severity) {
      params.append('severity', filter.severity);
    }
    if (filter?.type) {
      params.append('type', filter.type);
    }
    if (filter?.acknowledged !== undefined) {
      params.append('acknowledged', filter.acknowledged.toString());
    }

    const query = params.toString();
    // GET /api/v1/alerts (socket-cloud-alerts-monitoring.openapi.yaml; GH #2284).
    const url = `/api/v1/alerts${query ? `?${query}` : ''}`;
    
    // Browser-token-storage hardening: cookie-only auth via credentials:'include'.
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    return fetch(url, {
      headers,
      credentials: 'include',
    }).then(async (res) => {
      if (!res.ok) {
        await checkAuthBroken(res);
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const data = await res.json();
      return { success: true, data, timestamp: new Date() };
    }).catch((error) => ({
      success: false,
      error: error.message,
      timestamp: new Date(),
    }));
  }

  // PUT /api/v1/alerts/{alertId} with an AcknowledgeAlertRequest body
  // (socket-cloud-alerts-monitoring.openapi.yaml, operationId acknowledgeAlert;
  // GH #2712). The server records the caller as acknowledgedBy.
  async acknowledgeAlert(alertId: string): Promise<ApiResponse<void>> {
    const body: SocketCloudAlertsMonitoringTypes.components['schemas']['AcknowledgeAlertRequest'] = {
      acknowledged: true,
    };
    return this.request<void>(`/alerts/${alertId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  // POST /api/v1/alerts/{alertId}/resolve (operationId resolveAlert). The
  // ResolveAlertRequest body is optional; an empty object is sent because the
  // JSON content type this client always sets makes the API refuse an absent body.
  async resolveAlert(alertId: string): Promise<ApiResponse<void>> {
    const body: SocketCloudAlertsMonitoringTypes.components['schemas']['ResolveAlertRequest'] = {};
    return this.request<void>(`/alerts/${alertId}/resolve`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Health Check API: GET /api/v1/discovery/health answers socket-cloud-api's
  // status, uptime, build and database check, unauthenticated
  // (socket-cloud-discovery-fabric.openapi.yaml). The spec's bare /health is a
  // probe path; on socket.aaim.com the load balancer sends only /api/* and
  // /socket.io/* to the API, so a browser asking for /health reaches the
  // console container.
  async getHealthStatus(): Promise<ApiResponse<HealthResponse>> {
    return this.request<HealthResponse>('/discovery/health');
  }

  // User Management APIs
  async getUsers(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
    tenantId?: string;
  }): Promise<ApiResponse<{
    users: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.tenantId) searchParams.append('tenantId', params.tenantId);

    const query = searchParams.toString();
    return this.request(`/users${query ? `?${query}` : ''}`);
  }

  async getUser(userId: string): Promise<ApiResponse<any>> {
    return this.request(`/users/${userId}`);
  }

  async createUser(userData: {
    email: string;
    name: string;
    password?: string;
    tenantId: string;
    roleId: string;
    sendInvite?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.request(`/users`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(userId: string, updates: {
    name?: string;
    email?: string;
    status?: string;
  }): Promise<ApiResponse<any>> {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async updateUserRole(userId: string, tenantId: string, roleId: string): Promise<ApiResponse<any>> {
    return this.request(`/users/${userId}/tenant/${tenantId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ roleId }),
    });
  }

  async changeUserPassword(userId: string, currentPassword: string, newPassword: string): Promise<ApiResponse<any>> {
    return this.request(`/users/${userId}/password`, {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async deleteUser(userId: string): Promise<ApiResponse<void>> {
    return this.request(`/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async terminateUserSessions(userId: string): Promise<ApiResponse<any>> {
    return this.request(`/users/${userId}/sessions`, {
      method: 'DELETE',
    });
  }

  // Tenant Management APIs
  // listTenants (socket-cloud-organization.openapi.yaml): page and pageSize in,
  // with search, status and plan filters; TenantListResponse
  // { data, total, page, pageSize } out.
  async getTenants(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: SocketCloudOrganizationTypes.components['schemas']['TenantStatus'];
    plan?: SocketCloudOrganizationTypes.components['schemas']['TenantPlan'];
  }): Promise<ApiResponse<SocketCloudOrganizationTypes.components['schemas']['TenantListResponse']>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.plan) searchParams.append('plan', params.plan);

    const query = searchParams.toString();
    return this.request(`/tenants${query ? `?${query}` : ''}`);
  }

  async getTenant(
    tenantId: string,
  ): Promise<ApiResponse<SocketCloudOrganizationTypes.components['schemas']['TenantDetail']>> {
    return this.request(`/tenants/${tenantId}`);
  }

  async createTenant(tenantData: {
    name: string;
    slug: string;
    description?: string;
    plan?: string;
    maxUsers?: number;
    maxStorage?: number;
    maxNodes?: number;
    settings?: any;
    features?: string[];
  }): Promise<ApiResponse<any>> {
    return this.request(`/tenants`, {
      method: 'POST',
      body: JSON.stringify(tenantData),
    });
  }

  async updateTenant(tenantId: string, updates: {
    name?: string;
    description?: string;
    status?: string;
    plan?: string;
    maxUsers?: number;
    maxStorage?: number;
    maxNodes?: number;
    settings?: any;
    features?: string[];
  }): Promise<ApiResponse<any>> {
    return this.request(`/tenants/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteTenant(tenantId: string): Promise<ApiResponse<void>> {
    return this.request(`/tenants/${tenantId}`, {
      method: 'DELETE',
    });
  }

  // Tenant Role Management APIs
  async getTenantRoles(tenantId: string): Promise<ApiResponse<{ roles: any[] }>> {
    return this.request(`/tenants/${tenantId}/roles`);
  }

  async createTenantRole(tenantId: string, roleData: {
    name: string;
    description: string;
    permissions: string[];
  }): Promise<ApiResponse<any>> {
    return this.request(`/tenants/${tenantId}/roles`, {
      method: 'POST',
      body: JSON.stringify(roleData),
    });
  }

  async updateTenantRole(tenantId: string, roleId: string, updates: {
    name?: string;
    description?: string;
    permissions?: string[];
  }): Promise<ApiResponse<any>> {
    return this.request(`/tenants/${tenantId}/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteTenantRole(tenantId: string, roleId: string): Promise<ApiResponse<void>> {
    return this.request(`/tenants/${tenantId}/roles/${roleId}`, {
      method: 'DELETE',
    });
  }

  async inviteUserToTenant(tenantId: string, inviteData: {
    email: string;
    roleId: string;
    message?: string;
  }): Promise<ApiResponse<any>> {
    return this.request(`/tenants/${tenantId}/invite`, {
      method: 'POST',
      body: JSON.stringify(inviteData),
    });
  }

  // GET /api/v1/metrics (socket-cloud.openapi.yaml, GH #2709 D4): node metric
  // samples as JSON. The API stores only each node's latest snapshot, so a
  // node yields at most one sample per metric inside the window.
  async getMetrics(
    nodeId?: string,
    metric?: SocketCloudSchemaTypes.components['schemas']['NodeMetricName'],
    windowMinutes = 60,
  ): Promise<ApiResponse<SocketCloudSchemaTypes.components['schemas']['NodeMetricSample'][]>> {
    const params = new URLSearchParams({ windowMinutes: String(windowMinutes) });
    if (nodeId) {
      params.append('nodeId', nodeId);
    }
    if (metric) {
      params.append('metric', metric);
    }

    return this.request<SocketCloudSchemaTypes.components['schemas']['NodeMetricSample'][]>(
      `/metrics?${params.toString()}`,
    );
  }

  // Backup & Recovery APIs
  async createBackup(): Promise<ApiResponse<{ backupId: string; size: number; timestamp: Date }>> {
    return this.request<{ backupId: string; size: number; timestamp: Date }>('/backup/create', {
      method: 'POST',
    });
  }

  async getBackups(): Promise<ApiResponse<{ id: string; size: number; timestamp: Date }[]>> {
    return this.request<{ id: string; size: number; timestamp: Date }[]>('/backup/list');
  }

  async restoreBackup(backupId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/backup/restore/${backupId}`, {
      method: 'POST',
    });
  }
}

// Singleton instance
export const apiClient = new ApiClient();

// GraphQL client for complex queries
class GraphQLClient {
  private endpoint: string;

  constructor(endpoint = 'http://localhost:3000/graphql') {
    this.endpoint = endpoint;
  }

  async query<T>(query: string, variables?: Record<string, any>): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query, variables }),
      });

      const result = await response.json();

      if (result.errors) {
        return {
          success: false,
          error: result.errors.map((e: any) => e.message).join(', '),
          timestamp: new Date(),
        };
      }

      return {
        success: true,
        data: result.data,
        timestamp: new Date(),
      };
    } catch (error) {
      return {
        success: false,
        error: (error as Error).message,
        timestamp: new Date(),
      };
    }
  }
}

export const graphqlClient = new GraphQLClient();

// Common GraphQL queries
export const QUERIES = {
  GET_CLUSTER_OVERVIEW: `
    query GetClusterOverview {
      cluster {
        nodes {
          id
          name
          status
          role
          metrics {
            cpu { usage }
            memory { usage }
            network { latency }
          }
        }
        consensus {
          currentTerm
          role
          leaderAddress
        }
        alerts(severity: ["error", "critical"]) {
          id
          type
          severity
          message
          timestamp
        }
      }
    }
  `,
  
  GET_NODE_DETAILS: `
    query GetNodeDetails($nodeId: ID!) {
      node(id: $nodeId) {
        id
        name
        address
        port
        status
        role
        version
        uptime
        metrics {
          cpu { usage cores load }
          memory { used total usage }
          network { bytesIn bytesOut connectionsActive latency }
          storage { used total usage iops }
        }
        connections {
          to
          status
          latency
          bandwidth
        }
      }
    }
  `,
  
  GET_CRDT_OVERVIEW: `
    query GetCRDTOverview {
      crdts {
        states {
          id
          type
          replicaId
          lastModified
          vectorClock
          operations {
            id
            type
            timestamp
            replicaId
          }
        }
        metrics {
          totalOperations
          averageLatency
          conflictRate
          convergenceTime
        }
        anomalies {
          type
          severity
          description
          timestamp
        }
      }
    }
  `,
  
  GET_CRDT_PERFORMANCE: `
    query GetCRDTPerformance($timeRange: String!) {
      crdtPerformance(timeRange: $timeRange) {
        timestamp
        throughput {
          operationsPerSecond
          mergesPerSecond
          conflictsPerSecond
        }
        latency {
          average
          p95
          p99
          max
        }
        memory {
          totalUsage
          garbageCollectionEvents
        }
        network {
          bandwidth
          replicationLag
        }
      }
    }
  `,
};
// ---------------------------------------------------------------------------
// Operator sign-in (arc socket-cloud-operator-sso, sso-04).
//
// Two calls, both same-origin, both cookie-only. The console holds NO token at
// any point: `/sso/start` answers with an authorization URL and nothing else,
// and `/sso/exchange` delivers the session exclusively in Set-Cookie
// (.claude/rules/no-browser-token-storage.md,
//  .claude/rules/cookie-session-auth-canon.md).
//
// Shapes come from the generated types rather than being restated here; the
// contract is schema-registry/socket-cloud.openapi.yaml, whose path keys now
// carry the full prefix -- /api/v1/auth/sso/start and /api/v1/auth/sso/exchange
// -- beside a BARE servers origin (GH #2844, operator ruling 2026-09-15,
// .claude/rules/api-version-canonical.md). The live routes did not move: this
// file composes every URL from a hand-written endpoint literal, never from a
// spec path key, so the re-prefixing is a contract-shape change with no
// request-URL effect here.
//
// The auth base: a set VITE_API_URL is a base WITHOUT the version segment, and
// `/v1/...` is appended; unset or empty, the request goes same-origin to the
// literal `/api/v1/...` route. Every tracked build leaves it unset or empty, and
// there `/sso/start`, `/sso/exchange` and the `/api/v1/auth/me` hydration that
// follows an exchange all go same-origin. Under a set value the sign-in uses
// two bases: since P27 slice 1 (GH #4133) `AuthContext` sends `/api/v1/auth/me`
// and `/api/v1/auth/logout` same-origin whatever the variable holds, while these
// two helpers still follow it. The exchange then sets the session cookie for the
// configured API's host, and the hydration goes to the page's own origin (under
// `vite dev`, its `/api` proxy in vite.config.js). It finds the session only
// when the page shares that API's host, since the cookie is host-only and not
// port-scoped, and the page's origin forwards `/api` to that same API. The
// variable is read at call time, and the same-origin literal carries the whole
// prefix, so no base holds a path (api-version-canonical.md, G-E leg (a)). (The
// `ApiClient` above takes VITE_API_URL as a base that already carries `/v1`;
// that difference is not widened here.)
//
// Both FAIL CLOSED. A non-OK status, an unparseable body, or a body missing the
// field the contract requires throws. Nothing is synthesized to let a caller
// proceed (.claude/rules/no-fabricated-values.md).
// ---------------------------------------------------------------------------

export type SsoStartResponse =
  SocketCloudSchemaTypes.components['schemas']['SsoStartResponse'];
export type SsoExchangeResponse =
  SocketCloudSchemaTypes.components['schemas']['SsoExchangeResponse'];

/** Read the server's error message when it sent one, without inventing a cause. */
async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { message?: unknown; error?: unknown };
    if (typeof body?.message === 'string' && body.message.length > 0) {
      return body.message;
    }
    if (typeof body?.error === 'string' && body.error.length > 0) {
      return body.error;
    }
  } catch {
    // Non-JSON error body -- keep the caller's generic message.
  }
  return fallback;
}

/**
 * Begin an Aaim Workspace sign-in.
 *
 * Sends no body and no identifier: the route answers every caller identically,
 * so this request cannot be used to ask whether an account exists. Returns the
 * authorization URL the browser then navigates to, and nothing else.
 */
export async function startWorkspaceSso(): Promise<SsoStartResponse> {
  const apiUrl = import.meta.env.VITE_API_URL;
  const startInit: RequestInit = { method: 'POST', credentials: 'include' };
  const response = apiUrl
    ? await fetch(`${apiUrl}/v1/auth/sso/start`, startInit)
    : await fetch('/api/v1/auth/sso/start', startInit);

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, 'Sign-in could not be started. Try again shortly.'),
    );
  }

  const data = (await response.json()) as { authorizationUrl?: unknown };
  if (typeof data?.authorizationUrl !== 'string' || data.authorizationUrl.length === 0) {
    // A 200 with no authorization URL resolves nowhere. Failing here rather
    // than navigating to a synthesized URL keeps the destination the identity
    // provider's to decide.
    throw new Error('Sign-in could not be started. Try again shortly.');
  }

  return { authorizationUrl: data.authorizationUrl };
}

/**
 * Redeem the one-time handoff code for a session.
 *
 * `credentials: 'include'` is load-bearing in both directions: it carries the
 * sc-sso-pending flow marker the route requires, and it accepts the session
 * cookies the route sets. The response body carries the operator identity and
 * no credential of any kind; the contract declares additionalProperties false
 * so that is checkable rather than only stated.
 */
export async function exchangeSsoCode(code: string): Promise<SsoExchangeResponse> {
  const apiUrl = import.meta.env.VITE_API_URL;
  const exchangeInit: RequestInit = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  };
  const response = apiUrl
    ? await fetch(`${apiUrl}/v1/auth/sso/exchange`, exchangeInit)
    : await fetch('/api/v1/auth/sso/exchange', exchangeInit);

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(
        response,
        'The sign-in could not be completed. Start again from the sign-in page.',
      ),
    );
  }

  const data = (await response.json()) as SsoExchangeResponse | null;
  if (!data || typeof data.user?.id !== 'string' || data.user.id.length === 0) {
    // A 200 carrying no operator identity is not a completed sign-in.
    throw new Error('The sign-in could not be completed. Start again from the sign-in page.');
  }

  return data;
}

/**
 * The navigation seam for leaving the SPA for the identity provider.
 *
 * A plain `window.location.assign` call is untestable in jsdom and unmockable
 * without reaching into the Location object, so the departure is expressed as
 * one replaceable function -- the same ports-and-adapters shape the API side of
 * this flow uses for its upstream calls. Production behavior is a full-page
 * navigation and nothing else.
 */
export const ssoNavigation = {
  go(url: string): void {
    window.location.assign(url);
  },
};
